import { useNavigate } from 'react-router-dom';

import Icon from '@/components/ui/icon';
import logo from '@/assets/Logo StenPro.svg';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AppSwitcher,
  appRegistry,
  filterAppRegistryByAccess,
  useAuthContext,
} from '@stenpro/ui-components';

type HeaderProps = {
  onMenuClick?: () => void;
  onCommandPaletteOpen?: () => void;
};

export default function Header({
  onMenuClick,
  onCommandPaletteOpen,
}: HeaderProps) {
  const user = useAuth();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { logout, state } = useAuthContext();
  const switcherApps = filterAppRegistryByAccess(appRegistry, {
    isAuthenticated: state.isAuthenticated,
    roles: state.user?.roles ?? user?.roles,
    scopes: state.user?.scopes,
    currentAppId: 'project',
  });

  const openCommandPalette = () => {
    onCommandPaletteOpen?.();
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Failed to log out', err);
    }
    navigate('/login');
  };

  const goToProfile = () => {
    if (user?.userId) {
      navigate(`/users/${user.userId}`);
    }
  };

  const initial = user?.displayName?.charAt(0)?.toUpperCase() ?? '?';

  return (
    <header className="w-full h-16 bg-background border-b flex items-center px-6 justify-between shadow-sm z-30 relative">
      <button
        className="md:hidden p-2 -ml-2 mr-2 rounded text-blue-900 hover:bg-gray-100 transition"
        onClick={onMenuClick}
        aria-label="Öppna meny"
      >
        <Icon name="Menu" size={28} />
      </button>
      <div className="h-10 flex items-center">
        <img
          src={logo}
          alt="StenPro"
          className="h-full object-contain"
        />
      </div>
      <div className="flex-1 px-4 hidden md:block">
        <button
          type="button"
          onClick={openCommandPalette}
          className="flex w-full items-center gap-3 rounded-md border border-input bg-background px-4 py-2 text-sm text-muted-foreground shadow-sm transition hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Öppna kommandopalett"
        >
          <Icon name="Search" className="h-4 w-4" aria-hidden="true" />
          <span className="flex-1 text-left">Sök i StenPro</span>
          <span className="hidden items-center gap-1 text-xs text-muted-foreground lg:flex">
            <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[10px] uppercase">Ctrl</kbd>
            <span>+</span>
            <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[10px] uppercase">K</kbd>
          </span>
        </button>
      </div>
      <div className="flex items-center gap-4 relative">
        <AppSwitcher
          currentAppId="project"
          apps={switcherApps}
          placement="inline"
        />
        <button
          type="button"
          className="md:hidden p-2 rounded hover:bg-gray-100 transition"
          aria-label="Öppna kommandopalett"
          onClick={openCommandPalette}
        >
          <Icon name="Search" />
        </button>
        <button
          onClick={toggleTheme}
          className="p-2 rounded hover:bg-gray-100 transition"
          aria-label="Växla tema"
        >
          {theme === 'dark' ? <Icon name="Sun" /> : <Icon name="Moon" />}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 px-3 py-1 rounded-full hover:bg-gray-100 transition"
            >
              <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                {initial}
              </div>
              <span className="text-sm text-gray-700 font-medium">
                {user?.displayName ?? 'Gäst'}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onSelect={() => goToProfile()}>
              Min profil
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                void handleLogout();
              }}
              variant="destructive"
            >
              Logga ut
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

